import React, {
    Component,
    Fragment,
    createRef,
} from 'react'
import {
    compose,
    withProps,
} from 'recompose'
import {
    withGoogleMap,
    GoogleMap,
    Marker,
    withScriptjs,
    Circle,
    InfoWindow,
} from 'react-google-maps'

import store from '../../admin/store'
import SportVenuePreloaderBox from '../../front/components/shared/sportvenues/preloader-box'
import { INVESTMENT_STORE_KEY } from '../../admin/reducers/sport-venues-front'

const config = window.APP_CONFIG || {}

export const ZOOM_LEVEL = 11
const MAP_DEFAULT_PIN = `${config.REACT_APP_IMAGES_FOLDER}icons/gmap-pin-default.png`
const MAP_HOVER_PIN = `${config.REACT_APP_IMAGES_FOLDER}icons/gmap-pin-hover.png`
export const DEFAULT_LAT = parseFloat(config.REACT_APP_DEFAULT_LAT)
export const DEFAULT_LNG = parseFloat(config.REACT_APP_DEFAULT_LNG)
const DEFAULT_CITY_NAME = config.REACT_APP_DEFAULT_CITY_FILTER_NAME

export const DEFAULT_PREVIEW_OPTIONS = (props = {}) => ({
    zoom: typeof props.zoom !== 'undefined' ? props.zoom : 12,
    scrollwheel: typeof props.scrollwheel !== 'undefined' ? props.scrollwheel : false,
    center: {
        lat: DEFAULT_LAT,
        lng: DEFAULT_LNG,
    },
    mapTypeControl: typeof props.typeControl !== 'undefined' ? props.typeControl : true,
    mapTypeControlOptions: {
        style: window.google && window.google.maps ? window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR : '',
        position: window.google && window.google.maps ? window.google.maps.ControlPosition.RIGHT_TOP : '',
    },
    fullscreenControl: typeof props.fullscreenControl !== 'undefined' ? props.fullscreenControl : true,
    fullscreenControlOptions: {
        position: window.google && window.google.maps ? window.google.maps.ControlPosition.RIGHT_CENTER : '',
    },
    streetViewControl: false,
    disableDefaultUI: typeof props.disableUI !== 'undefined' ? props.disableUI : false,
})

/**
 * Set marker hovered by ID of entity
 *
 * @param {Array} markers
 * @param {int} id
 * @param {boolean} isHovered
 * @return {*}
 */
export const hoverMarkers = (markers, id, isHovered = false) => markers.map(marker => {
    if (marker.id === id) {
        marker.isHovered = isHovered
    } else {
        marker.isHovered = false
    }

    return marker
})

/**
 * Pass markers to specified object
 *
 * @param {Object} entity
 * @param {string} entityName - for ex. event - group - ...
 * @param {boolean} passToCity - set if pass markers only to city
 * @returns {Array}
 */
export const parseMarkers = (entity, entityName = '', passToCity = true) => {
    if (!entity) {
        return []
    }

    const {
        data,
    } = entity
    let markers = []

    const dataElements = Array.isArray(data) ? data : Object.values(data)

    dataElements.filter(element => element.locations)
        .forEach(element => {
            const locations = element.locations.data
            if (!locations) {
                return
            }

            const cityName = DEFAULT_CITY_NAME ? DEFAULT_CITY_NAME.toLowerCase() : null

            locations.filter(location => passToCity && cityName ? (location.formatted_address && location.formatted_address.toLowerCase().indexOf(cityName) !== -1) : true)
                .forEach(location => {
                    markers.push(parseSingleMarker(location, `${entityName}${element?.slug || element.id}`))
                })
        })

    return markers
}

/**
 * Parse single markers
 *
 * @param {Object} location
 * @param {int} id
 * @return {Object}
 */
export const parseSingleMarker = (location, id) => ({
    'id': id,
    'lat': location.latitude,
    'lng': location.longitude,
    'isHovered': false,
})

/**
 * Set markers bounds
 *
 * @param {Array} markers
 * @param {bool} fillWithEmptyBounds
 * @param {float} boundsSize
 * @returns {window.google.maps.LatLngBounds}
 */
const setBounds = (markers, fillWithEmptyBounds, boundsSize = 0.02) => {
    let bounds = new window.google.maps.LatLngBounds()

    markers.forEach(marker => {
        bounds.extend({
            'lat': marker.lat,
            'lng': marker.lng,
        })
    })

    // Don't zoom in too far on only one marker
    if (bounds.getNorthEast() && bounds.getNorthEast().equals(bounds.getSouthWest()) && window.google && window.google.maps && fillWithEmptyBounds !== false) {
        let extendPoint1 = new window.google.maps.LatLng(bounds.getNorthEast().lat() + boundsSize, bounds.getNorthEast().lng() + boundsSize)
        let extendPoint2 = new window.google.maps.LatLng(bounds.getNorthEast().lat() - boundsSize, bounds.getNorthEast().lng() - boundsSize)
        bounds.extend(extendPoint1)
        bounds.extend(extendPoint2)
    }

    return bounds
}


/**
 * Map
 */
class Map extends Component {
    constructor() {
        super()

        this.circle = createRef()
        this.mapRef = createRef()

        this.state = {
            markerOpen: false,
            infoIndex: null,
            hoverOnMarker: false,
            hoverIndex: null,
            forceMapCenter: null,
        }
    }

    componentDidMount() {
        store.dispatch({
            type: 'SET_MAP_LOADED',
            loading: false,
        })

        this.setDefaultBounds()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.markers !== this.props.markers) {
            this.setDefaultBounds()
        }
    }

    renderVenueBox = markerId => {
        const sportVenueSlug = markerId.replace('sportVenue', '')

        return (
            <SportVenuePreloaderBox
                sportVenueSlug={sportVenueSlug}
                {...this.props}
                mainClassName="Groups-list"
                toggleHoverMarker={() => {}}
                sportVenueType={INVESTMENT_STORE_KEY}
            />
        )
    }

    /**
     * latlng is the apparent centre-point
     * offsetx is the distance you want that point to move to the right, in pixels
     * offsety is the distance you want that point to move upwards, in pixels
     * offset can be negative
     * offsetx and offsety are both optional
     *
     * @param {Object} latLng
     * @param {Number} offsetX
     * @param {Number} offsetY
     */
    offsetCenter = (latLng, offsetX, offsetY) => {
        if (!window?.google?.maps || !this.mapRef?.current) {
            return
        }

        const scale = Math.pow(2, this.mapRef.current.getZoom())

        const worldCoordinateCenter = this.mapRef.current.getProjection().fromLatLngToPoint(new window.google.maps.LatLng(latLng))
        const pixelOffset = new window.google.maps.Point((offsetX/scale) || 0, (offsetY/scale) || 0)

        const worldCoordinateNewCenter = new window.google.maps.Point(
            worldCoordinateCenter.x - pixelOffset.x,
            worldCoordinateCenter.y + pixelOffset.y
        )

        const newCenter = this.mapRef.current.getProjection().fromPointToLatLng(worldCoordinateNewCenter)

        const newCenterLatLng = {
            lat: newCenter.lat(),
            lng: newCenter.lng(),
        }

        this.setState({ forceMapCenter: newCenterLatLng })
    }

    /**
     * Display or hide info window after a click on the marker
     *
     * @param {number} index
     * @param {Object|null} marker
     */
    toggleInfoWindow = (index, marker = null) => {
        const newStateMarkerOpen = !this.state.markerOpen

        this.setState({
            markerOpen: newStateMarkerOpen,
            infoIndex: index,
        })

        if (marker && newStateMarkerOpen) {
            this.offsetCenter(marker, 0, -200)
        }

        if (!newStateMarkerOpen) {
            this.setState({ forceMapCenter: null }, () => this.setDefaultBounds())
        }
    }

    /**
     * Set default bounds based on props
     *
     * @param {GoogleMap} mapRef    -  if is not defined, take map from this.mapRef
     */
    setDefaultBounds = (mapRef = null) => {
        const {
            markers,
            fitBounds,
            fillWithEmptyBounds,
            radius,
            fitCircle,
            boundsSize,
        } = this.props

        const map = mapRef || this.mapRef?.current

        if (fitBounds !== false && map && markers && markers.length > 0) {
            map.fitBounds(setBounds(markers, fillWithEmptyBounds, boundsSize || 0.02))
        }

        if (radius && map && this.circle && markers && markers.length > 0 && fitBounds === false && fitCircle === true) {
            map.fitBounds(this.circle.getBounds())
        }
    }

    render() {
        const {
            markerOpen,
            infoIndex,
            forceMapCenter,
        } = this.state

        const {
            markers,
            options,
            radius,
            windowItems,
            isHoverable,
            clickableMarkers,
            maxZoomProps,
        } = this.props

        let center = {}

        if (!markers && !forceMapCenter) {
            center = {
                center: {
                    lat: DEFAULT_LAT,
                    lng: DEFAULT_LNG,
                },
            }
        }

        if (forceMapCenter) {
            center = {
                center: forceMapCenter,
            }
        }

        return (
            <GoogleMap
                ref={this.mapRef}
                options={
                    options || {
                        ...center,
                        zoom: ZOOM_LEVEL,
                        maxZoom: maxZoomProps || (markers && markers.length > 3 ? ZOOM_LEVEL : ZOOM_LEVEL + (markers ? (4 - markers.length) : 0)),
                        scrollwheel: false,
                        mapTypeControl: false,
                        fullscreenControl: true,
                        fullscreenControlOptions: {
                            position: window.google && window.google.maps ? window.google.maps.ControlPosition.RIGHT_TOP : '',
                        },
                        streetViewControl: false,
                    }
                }
                onClick={() => {
                    if (this.state.markerOpen) {
                        this.toggleInfoWindow()
                    }
                }}
            >
                {markers && markers.map((marker, index) =>
                    <Fragment key={`marker-${index}`}>
                        <Marker
                            position={{
                                lat: marker.lat,
                                lng: marker.lng,
                            }}
                            onMouseOver={isHoverable ? () => marker.isHovered = true : null}
                            onMouseOut={isHoverable ? () => marker.isHovered = false : null}
                            icon={marker.isHovered ? MAP_HOVER_PIN : MAP_DEFAULT_PIN}
                            zIndex={marker.isHovered ? 10 : 0}
                            onClick={!clickableMarkers ? null : () => this.toggleInfoWindow(index, marker)}
                        >
                            {markerOpen && infoIndex === index &&
                                <InfoWindow onCloseClick={() => this.toggleInfoWindow()}>
                                    {this.renderVenueBox(marker.id, windowItems)}
                                </InfoWindow>
                            }
                        </Marker>

                        {radius &&
                            <Circle
                                center={{
                                    lat: marker.lat,
                                    lng: marker.lng,
                                }}
                                draggable={false}
                                editable={false}
                                options={{
                                    center: {
                                        lat: marker.lat,
                                        lng: marker.lng,
                                    },
                                    radius: radius * 1000,
                                }}
                                radius={radius * 1000}
                                visible={true}
                                ref={node => this.circle = node}
                            />
                        }
                    </Fragment>
                )}
            </GoogleMap>
        )
    }
}

export default compose(
    withProps(props => ({
        loadingElement: props.loadingElement || <Fragment />,
        containerElement: props.containerElement || <div className="h-100" style={props.style} />,
        mapElement: props.mapElement || <div className="h-100 map-element" />,
        googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${config.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,drawing,places`,
    })),
    withScriptjs,
    withGoogleMap,
)(props => <Map {...props} />)
